import { useEffect, useCallback, useState, useRef } from 'preact/hooks';
import { useAssetWebsterChat } from '@/hooks/useAssetWebsterChat';
import { useLandingPage } from '@/hooks/useLandingPage';
import { useLandingPageSignalR } from '@/hooks/useLandingPageSignalR';
import { STATUS_MESSAGES } from '@/hooks/useStreamingStatus';
export function useLandingPageChat({ clientCompanyId, workspaceId, campaignId, webId, onRebuilt: onRebuiltExternal }) {
    const landingPageHook = useLandingPage({ clientCompanyId, workspaceId, campaignId, webId });
    // Shared chat lifecycle. LP creates its session server-side as part of
    // generation, so no createSession factory — sendMessage no-ops until the
    // session id is captured from the LP DTO.
    const assetChat = useAssetWebsterChat({
        clientCompanyId,
        workspaceId,
        initialSessionId: null,
    });
    const { chat, streamingStatus } = assetChat;
    const { streamMsgId, resultHandledRef, hadRebuildRef, updateStatus, beginStreaming } = streamingStatus;
    const [thankYouGenerating, setThankYouGenerating] = useState(false);
    const [suggestThankYouGeneration, setSuggestThankYouGeneration] = useState(false);
    // Track the in-flight API call so onStreamResult can wait for it
    const apiCallRef = useRef(null);
    // ---- Agent-stream events (LP first generation) ----------------------
    const onThinking = useCallback(() => {
        updateStatus('thinking', STATUS_MESSAGES.thinking);
    }, [updateStatus]);
    const onContent = useCallback(() => {
        updateStatus('content', STATUS_MESSAGES.content);
    }, [updateStatus]);
    const onToolUse = useCallback(() => {
        updateStatus('tool_use', STATUS_MESSAGES.tool_use);
    }, [updateStatus]);
    const onToolResult = useCallback(() => {
        updateStatus('thinking', STATUS_MESSAGES.thinking);
    }, [updateStatus]);
    const onStreamResult = useCallback(async () => {
        // Capture whether the conversational response already finalized this turn's
        // chat bubble BEFORE we flip resultHandledRef. If so, we're in chat-rebuild
        // mode: the personalized response is a finalized bubble above the working
        // indicator, so we just remove the indicator — no need to write the same
        // content into a second bubble.
        const conversationalResponseFinalizedThisTurn = resultHandledRef.current;
        resultHandledRef.current = true;
        // Wait for the API call to finish — could be createAndGenerate (returns void)
        // or aiChatSendMessage (returns AIConversationSessionDto)
        let session = null;
        if (apiCallRef.current) {
            try {
                session = await apiCallRef.current;
            }
            catch (err) {
                console.error('API call failed during stream result:', err);
            }
            apiCallRef.current = null;
        }
        // If we got a session DTO back (chat flow), read the suggest flag
        if (session && typeof session === 'object' && 'SuggestThankYouGeneration' in session) {
            setSuggestThankYouGeneration(!!session.SuggestThankYouGeneration);
        }
        // Phase 1: Finalize the chat presentation. Two cases:
        if (conversationalResponseFinalizedThisTurn) {
            // Conversational response already finalized the bubble this turn. Only
            // remove if it's the transient working indicator (rebuild-prefixed) —
            // for first-gen with AI completion the conversationalResponse reused
            // the original stream id, and that bubble holds the message we want
            // the user to see.
            if (streamMsgId.current.startsWith('rebuild-')) {
                chat.removeMessage(streamMsgId.current);
            }
            chat.setGenerating(false);
        }
        else {
            // First-generation (and any non-conversational) flow: the visible Webster reply
            // is the agent's inline <message> closer, delivered + persisted via the
            // assistantMessageAppended SignalR event (same as email). So just tear down the
            // working bubble here. Finalizing it with a canned "I've generated your landing
            // page!" produced a stray, never-persisted message that duplicated the real
            // closer and vanished on reload — legacy from before LP adopted the inline
            // envelope. Lifecycle-only, mirroring useEmailAssetChat's onRebuilt.
            chat.removeMessage(streamMsgId.current);
            chat.setGenerating(false);
        }
        // Phase 2: Load data during blast-off animation (~4.5s window).
        const [freshLp] = await Promise.all([landingPageHook.load(), landingPageHook.loadDraft(), landingPageHook.loadContext()]);
        // Also refresh the WebAsset so DraftUpdatedAt stays current — the
        // Save-as-Version gate reads it to decide whether the draft has moved
        // ahead of the latest version. Without this, edit flows that resolve
        // through the result-stream path (rather than the Rebuilt SignalR
        // event) leave the asset stale and Save stays disabled.
        await onRebuiltExternal?.();
        // Capture session id (server may create it after firing result event)
        if (freshLp?.ConversationSessionId) {
            assetChat.captureSessionId(freshLp.ConversationSessionId);
        }
        else if (!assetChat.sessionId) {
            // Session not ready yet — poll briefly until the server creates it
            const pollForSession = async () => {
                for (let i = 0; i < 5; i++) {
                    await new Promise(r => setTimeout(r, 2000));
                    const polledLp = await landingPageHook.load();
                    if (polledLp?.ConversationSessionId) {
                        assetChat.captureSessionId(polledLp.ConversationSessionId);
                        return;
                    }
                }
            };
            pollForSession();
        }
    }, [landingPageHook, chat, assetChat, streamMsgId, resultHandledRef, onRebuiltExternal]);
    const onStreamError = useCallback((message) => {
        console.error('Landing page generation error:', message);
        // The agent-stream `error` event is generic — it can fire for LP first-gen,
        // chat-driven rebuilds, OR TY-only background generation (which has no chat
        // turn in flight). Only touch the chat if a chat turn is actually active;
        // otherwise we'd corrupt the previously-finalized bubble's content.
        if (chat.generating) {
            chat.updateStreamingMessage(streamMsgId.current, {
                content: 'Sorry, something went wrong generating your landing page. Please try again.',
                streamStatus: undefined,
            });
            chat.setGenerating(false);
            chat.finalizeStreamingMessage(streamMsgId.current);
        }
        // Defensive cleanup for non-chat surfaces driven by an in-flight generation.
        // The dedicated `generationFailed` / `thankYouGenerationFailed` events also
        // do this, but those don't fire for every error path — the agent service
        // can emit `error` without the queue trigger's onFailure handler running
        // (e.g. when the AI service returns the error inline rather than throwing).
        setThankYouGenerating(false);
        landingPageHook.load();
    }, [chat, streamMsgId, landingPageHook]);
    const onStreamDone = useCallback(() => {
        // Safety net — but only if onStreamResult hasn't already handled it.
        if (resultHandledRef.current)
            return;
        chat.setGenerating(false);
        chat.finalizeStreamingMessage(streamMsgId.current);
    }, [chat, streamMsgId, resultHandledRef]);
    // ---- Thank-you sub-stream -------------------------------------------
    const onThankYouGenerating = useCallback(() => {
        setThankYouGenerating(true);
    }, []);
    const onThankYouResult = useCallback(async () => {
        // Stop and finalize the chat thinking indicator the moment the thank-you page
        // returns. The generic stream events have no completion of their own for the
        // thank-you turn, so without this the bubble would keep ticking indefinitely.
        if (chat.generating) {
            chat.updateStreamingMessage(streamMsgId.current, {
                content: "I've created your thank you page. Take a look at the preview.",
                streamStatus: undefined,
            });
            chat.finalizeStreamingMessage(streamMsgId.current);
            chat.setGenerating(false);
        }
        await landingPageHook.loadThankYouDraft();
        await landingPageHook.load();
        setThankYouGenerating(false);
    }, [landingPageHook, chat, streamMsgId]);
    const onThankYouRebuilding = useCallback(() => {
        setThankYouGenerating(true);
    }, []);
    const onThankYouRebuilt = useCallback(async () => {
        // Mirror onThankYouResult: finalize the chat indicator so the timer stops when
        // a thank-you rebuild completes.
        if (chat.generating) {
            chat.updateStreamingMessage(streamMsgId.current, {
                content: "I've updated your thank you page. Take a look at the preview.",
                streamStatus: undefined,
            });
            chat.finalizeStreamingMessage(streamMsgId.current);
            chat.setGenerating(false);
        }
        await Promise.all([landingPageHook.loadThankYouDraft(), landingPageHook.load()]);
        await onRebuiltExternal?.();
        setThankYouGenerating(false);
    }, [landingPageHook, onRebuiltExternal, chat, streamMsgId]);
    const onGenerationFailed = useCallback((message) => {
        landingPageHook.load();
        onStreamError(message);
    }, [landingPageHook, onStreamError]);
    const onThankYouGenerationFailed = useCallback((message) => {
        console.error('Thank you page generation error:', message);
        landingPageHook.load();
        setThankYouGenerating(false);
        if (chat.generating) {
            chat.updateStreamingMessage(streamMsgId.current, {
                content: 'Sorry, something went wrong generating the thank you page. Please try again.',
                streamStatus: undefined,
            });
            chat.finalizeStreamingMessage(streamMsgId.current);
            chat.setGenerating(false);
        }
    }, [landingPageHook, chat, streamMsgId]);
    // ---- Chat-rebuild events --------------------------------------------
    // Mostly delegated to the shared hook; LP needs to refresh draft HTML on
    // rebuilt and use a custom rebuildError message.
    const onRebuilt = useCallback(async () => {
        // Webster's text reply already arrived via onConversationalResponse (fired before
        // the rebuild was enqueued) and finalized its own bubble; assetChat.onRebuilt
        // removes the transient rebuild-status bubble that onRebuilding opened beneath
        // it. We also refresh the draft HTML and WebAsset so DraftUpdatedAt is current.
        hadRebuildRef.current = true;
        assetChat.onRebuilt();
        await Promise.all([landingPageHook.loadDraft(), landingPageHook.load()]);
        await onRebuiltExternal?.();
    }, [assetChat, landingPageHook, hadRebuildRef, onRebuiltExternal]);
    const onRebuildError = useCallback((message) => {
        console.error('Landing page rebuild error:', message);
        chat.updateStreamingMessage(streamMsgId.current, {
            content: 'Sorry, something went wrong updating your page. Please try again.',
            streamStatus: undefined,
        });
        chat.setGenerating(false);
        chat.finalizeStreamingMessage(streamMsgId.current);
    }, [chat, streamMsgId]);
    // Subscribe to SignalR generation events
    useLandingPageSignalR({
        webId,
        onThinking,
        onContent,
        onToolUse,
        onToolResult,
        onResult: onStreamResult,
        onError: onStreamError,
        onDone: onStreamDone,
        onThankYouGenerating,
        onThankYouResult,
        onGenerationFailed,
        onThankYouGenerationFailed,
        onAnalyzing: assetChat.onAnalyzing,
        onRebuilding: assetChat.onRebuilding,
        onRebuilt,
        onThankYouRebuilding,
        onThankYouRebuilt,
        onRebuildError,
        onConversationalResponse: assetChat.onConversationalResponse,
        onAssistantMessageAppended: assetChat.onAssistantMessageAppended,
        // Multi-user turn-lock: chatStateChanged updates lockedByOther state for
        // spectators; userMessageAppended renders peer messages in real time so
        // the chat conveys "Joel just asked X" without waiting for a refresh.
        onChatStateChanged: assetChat.onChatStateChanged,
        onUserMessageAppended: assetChat.onUserMessageAppended,
    });
    // Initialize: load LP and context in parallel
    useEffect(() => {
        landingPageHook.load();
        landingPageHook.loadContext();
    }, []);
    // When the LP loads, capture its session id and resume mid-flight state.
    // Deps narrowed to landingPage.Id so this fires exactly once per loaded LP.
    useEffect(() => {
        if (landingPageHook.landingPage?.ConversationSessionId) {
            assetChat.captureSessionId(landingPageHook.landingPage.ConversationSessionId);
        }
        if (landingPageHook.landingPage?.GenerationStatus === 'Generating' && !chat.generating) {
            beginStreaming();
        }
        if (landingPageHook.landingPage?.ThankYouGenerationStatus === 'Generating') {
            setThankYouGenerating(true);
        }
    }, [landingPageHook.landingPage?.Id]);
    const isExternalMode = landingPageHook.landingPage?.Mode === 'External';
    const startGeneration = useCallback(async (settings, prompt) => {
        if (isExternalMode)
            return;
        if (prompt)
            chat.addUserMessage(prompt);
        beginStreaming();
        const request = prompt ? { ...settings, Prompt: prompt } : settings;
        // Store the promise so onStreamResult can wait for the save to complete
        apiCallRef.current = landingPageHook.createAndGenerate(request);
    }, [landingPageHook, chat, beginStreaming, isExternalMode]);
    /** Send a message — routes through the shared chat lifecycle. The shared
     *  sendMessage returns the session DTO; mirror it into apiCallRef so
     *  onStreamResult can read SuggestThankYouGeneration / Messages from it. */
    const sendMessage = useCallback(async (prompt) => {
        if (isExternalMode)
            return;
        const promise = assetChat.sendMessage(prompt);
        apiCallRef.current = promise;
        await promise;
    }, [assetChat, isExternalMode]);
    const loadPreview = useCallback(async () => {
        if (isExternalMode)
            return;
        await landingPageHook.loadDraft();
    }, [landingPageHook, isExternalMode]);
    /** Retry a failed generation */
    const retryGeneration = useCallback(async () => {
        if (isExternalMode)
            return;
        beginStreaming();
        apiCallRef.current = landingPageHook.regenerate();
    }, [landingPageHook, beginStreaming, isExternalMode]);
    /** Trigger explicit thank you page generation */
    const regenerateThankYou = useCallback(async (prompt) => {
        if (isExternalMode)
            return;
        setThankYouGenerating(true);
        setSuggestThankYouGeneration(false);
        // Open a fresh chat streaming turn so the thinking timer resets to 0 for the
        // thank-you turn. The server reuses the generic thinking/content stream events
        // for thank-you generation, so without a fresh turn those events keep writing
        // to the already-finished landing-page bubble (stale streamMsgId + stale
        // startedAt) — which is why the timer appeared to continue from the LP count.
        // onThankYouResult finalizes this bubble and stops the timer.
        beginStreaming();
        await landingPageHook.regenerateThankYou(prompt);
    }, [landingPageHook, isExternalMode, beginStreaming]);
    return {
        // Chat state
        messages: assetChat.messages,
        generating: assetChat.generating,
        hasGenerated: !!landingPageHook.landingPage,
        // Chat input gated until the session id is captured (after first generation)
        chatReady: !landingPageHook.landingPage || !!assetChat.sessionId,
        // Multi-user turn-lock — expose so the LP builder can compute
        // "locked by other" against the current user id and pass
        // lockedByUserName into WebsterChatInput. turnUserId is null when
        // turnState is Idle, so a simple !== currentUserId check suffices.
        turnState: assetChat.turnState,
        turnUserId: assetChat.turnUserId,
        turnUserName: assetChat.turnUserName,
        // Landing page state
        landingPage: landingPageHook.landingPage,
        setLandingPage: landingPageHook.setLandingPage,
        html: landingPageHook.html,
        loading: landingPageHook.loading,
        context: landingPageHook.context,
        contextLoading: landingPageHook.contextLoading,
        sessionId: assetChat.sessionId,
        // Thank you page state
        thankYouHtml: landingPageHook.thankYouHtml,
        thankYouGenerating,
        loadThankYouDraft: landingPageHook.loadThankYouDraft,
        updateThankYouSettings: landingPageHook.updateThankYouSettings,
        updatePageMeta: landingPageHook.updatePageMeta,
        regenerateThankYou,
        suggestThankYouGeneration,
        canGenerateThankYou: !!(landingPageHook.landingPage?.DraftBlobPath && (landingPageHook.landingPage.ThankYouGenerationStatus === 'Idle' || !landingPageHook.landingPage.ThankYouGenerationStatus)),
        // Generation status
        generationStatus: landingPageHook.landingPage?.GenerationStatus ?? null,
        generationError: landingPageHook.landingPage?.GenerationError ?? null,
        thankYouGenerationStatus: landingPageHook.landingPage?.ThankYouGenerationStatus ?? null,
        thankYouGenerationError: landingPageHook.landingPage?.ThankYouGenerationError ?? null,
        // Actions
        startGeneration,
        sendMessage,
        retryGeneration,
        loadPreview,
        updateSettings: landingPageHook.updateSettings,
        updateContext: landingPageHook.updateContext,
        loadContext: landingPageHook.loadContext,
        deleteLandingPage: landingPageHook.deleteLandingPage,
    };
}
